<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <v-container class="primary py-0 px-0" fluid>
    <v-text-field
      v-model="query"
      label="Recherche rapide"
      prepend-inner-icon="search"
      class="mx-3"
      color="info"
      clearable
    />

    <InfoTable
      :sort-by.sync="sortBySync"
      :sort-desc.sync="sortDescSync"
      :use-custom-sort="true"
      :items="results.items"
      :count="results.count"
      :is-last-page="false"
      :hide-common-headers="true"
      :with-context-menu="false"
      :show-more="showMore"
      :action="onClickItem"
      :custom-headers="getCustomHeaders(contentType)"
      :loading="loadingSearch"
      :pagination="pagination"
    />
  </v-container>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vue2-helpers/vuex';
import { useQuery } from '@vue/apollo-composable';

import InfoTable from '@/components/search/InfoTable.vue';

import { getCustomHeaders } from '@/utils/content';
import { getGraphqlSchema } from '../../../graphql/index';

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => { } },
  contentType: { type: Object, default: () => { } },
  onClickItem: { type: Function, default: () => { } }
});

const store = useStore();

const workspace = props.field.workspace || store.getters.workspace.code;

const sortBy = ref(null);
const sortBySync = computed({
  get () {
    return sortBy.value;
  },
  async set (value) {
    await nextTick();
    clearResults();
    sortBy.value = value;
  }
});

const sortDesc = ref(false);
const sortDescSync = computed({
  get () {
    return sortDesc.value;
  },
  async set (value) {
    await nextTick();
    clearResults();
    sortDesc.value = value;
  }
});
const isAlpha = computed(() => store.state.environment.PROJECT === 'alpha');
const query = ref('');
const start = ref(0);
const limit = 20;
const filters = ref([
  {
    terms: {
      wtype: [
        props.contentType.configs.wtype
      ]
    }
  },
  {
    terms: {
      wtype: [
        props.contentType.configs.wtype
      ]
    }
  },
  { term: { online: true } },
  { term: { status: 'published' } }
]);

if (!(isAlpha.value && props.field?.project)) {
  filters.value.push({
    terms: {
      workspace: [
        workspace
      ]
    }
  });
}

if (props.field.filters) {
  props.field.filters.forEach(f => {
    const filterFieldName = Object.getOwnPropertyNames(f.terms)[0];
    const sameFilterIndex = filters.value.findIndex(fa => fa?.terms?.hasOwnProperty(filterFieldName));

    if (sameFilterIndex >= 0) {
      filters.value[sameFilterIndex] = f;
    } else {
      filters.value.push(f);
    }
  });
}

const results = ref({
  count: 0,
  items: []
});

const clearResults = () => {
  start.value = 0;
  results.value.items = [];
};

const pagination = computed(() => ({ currentPage: (start.value / limit + 1), totalPages: Math.ceil(results.value.count / limit) }));

const fieldsToSearch = store.state.searchFields;

if (props.field.fieldsToSearch && props.field.fieldsToSearch.length) {
  const fieldsToSearchFromProps = ref(props.field.fieldsToSearch);

  if (!Array.isArray(fieldsToSearchFromProps.value)) fieldsToSearchFromProps.value = [fieldsToSearchFromProps.value];

  fieldsToSearchFromProps.value.forEach(field => {
    if (!fieldsToSearch.includes(field)) fieldsToSearch.push(field);
  });
}

const searchVariables = computed(() => {
  const searchVars = {
    query: {
      bool: {
        must: [
          {
            simple_query_string: {
              query: `${query.value || ''}*`,
              fields: fieldsToSearch
            }
          }
        ],
        filter: filters.value
      }
    },
    from: start.value,
    size: limit
  };

  // Format sorting in correct value
  // Sorting can make the must relevant query results not appear in the top
  const formattedSortBy = sortBy.value ? sortBy.value : null;
  const formattedSortDesc = sortDesc.value ? 'desc' : 'asc';

  if (formattedSortBy) {
    searchVars.sort = [
      { [formattedSortBy]: formattedSortDesc }
    ];
  }

  return searchVars;
});

const { onResult, loading: loadingSearch } = useQuery(
  getGraphqlSchema('facetedSearch', props.field.project),
  searchVariables,
  { enabled: true, fetchPolicy: 'no-cache' }
);

onResult(result => {
  if (result.loading) {
    return;
  }

  if (start.value > 0) {
    results.value.items = results.value.items.concat(result.data.hits.items);
  } else {
    results.value.count = result.data.hits.count;
    results.value.items = result.data.hits.items;
    results.value.facets = result.data.facets.facets;
  }
});

const showMore = () => {
  const newStart = start.value + limit;
  if (newStart < results.value.count) {
    start.value = newStart;
  }
};

watch(query, async (newValue, oldValue) => {
  if (newValue !== oldValue) {
    start.value = 0;
  }
});

</script>

<style lang="scss" scoped>

</style>
